/**
 * The user identity the UI should display and use for customer data paths.
 *
 * getAuthUser() and getUserId() return the effective customer identity:
 * - Normal login: the current Firebase user and their UID, unchanged.
 * - Admin impersonation: the customer being viewed, after server validation.
 *   Only uid, email, displayName and photoURL are substituted. Token methods
 *   remain bound to the isolated admin-session Firebase credential, so requests
 *   keep their session restrictions and attribution.
 *
 * An unresolved or switched admin session returns no user, never a cached target.
 * Code that needs the actual credential (for session checks or sign-out) must
 * use firebase.auth().currentUser directly instead of these getters.
 */
import firebase from "firebase/app";
import { isAdminSessionUid } from "../../../src/shared/adminSessionIdentity";

let effectiveUser = null;
let credentialUser = null;

/** Cache the server-validated customer identity for this exact Firebase credential. */
export function resolveEffectiveAuthUser(rawUser, accessResponse) {
  credentialUser = rawUser;
  if (!rawUser) return (effectiveUser = null);
  if (!isAdminSessionUid(rawUser.uid)) return (effectiveUser = rawUser);
  const identity = accessResponse?.effectiveUser;
  if (!accessResponse?.impersonation || !identity?.uid) {
    effectiveUser = null;
    throw new Error("The admin session has expired. Sign in again.");
  }
  effectiveUser = new Proxy(rawUser, {
    get(target, key) {
      if (["uid", "email", "displayName", "photoURL"].includes(key)) return identity[key] ?? null;
      const value = Reflect.get(target, key, target);
      return typeof value === "function" ? value.bind(target) : value;
    },
  });
  return effectiveUser;
}

/** Finish admin bootstrap without leaving a rejected Firebase login behind. */
export async function resolveAdminSessionOrSignOut(rawUser, accessResponse) {
  const auth = firebase.auth();
  // A response for a previous identity must neither replace nor sign out the
  // account that signed in while its access request was pending.
  if (auth.currentUser !== rawUser) return null;
  try {
    return resolveEffectiveAuthUser(rawUser, accessResponse);
  } catch {
    // No await separates the identity check from signOut. The caller must also
    // discard this result if auth changes while local sign-out is settling.
    await auth.signOut();
    return null;
  }
}

export function getAuthUser() {
  const rawUser = firebase.auth().currentUser;
  if (!isAdminSessionUid(rawUser?.uid)) return rawUser;
  // A cached target must never survive a Firebase account/session switch.
  return rawUser === credentialUser ? effectiveUser : null;
}

export function getUserId() {
  return getAuthUser()?.uid;
}
